import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  Autocomplete,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { getMomentTz } from "@dexteel/mesf-core";
import type { Moment } from "moment";
import { TaskDateSection } from "./TaskDateSection";
import type {
  EquipmentTreeRowFlat,
  SparePartTreeRow,
  PartInfo,
} from "../../models";
import { isSparePartRow } from "../../models";
import { getOperationBounds } from "../../repositories/SharedEqpRepository";

const moment = getMomentTz();

interface FormData {
  timestamp: Moment | null;
  isNewPart: boolean;
  selectedSparePart: SparePartTreeRow | null;
  serialNumber: string;
  make: string;
  model: string;
  vendor: string;
  warehouseNumber: string;
  drawingNumber: string;
  notes: string;
  operatorNumber: string;
}

/**
 * Row type that can be selected for installation.
 * Must have PositionId and PartTypeId to install a part.
 */
type InstallableRow = EquipmentTreeRowFlat & {
  PositionId: number;
  PartTypeId: number;
};

interface Props<T extends EquipmentTreeRowFlat> {
  open: boolean;
  selectedRow: T | null;
  treeData: T[];
  onClose: () => void;
  /**
   * Callback to get the parent part ID for installation.
   * Defaults to using EquipmentId if not provided.
   * Use this for modules like Sidewall where burner sub-components
   * need to be installed under their parent burner panel.
   */
  getParentPartId?: (row: T) => number;
  onInstallNewPart: (
    parentPartId: number,
    positionId: number,
    partTypeId: number,
    partInfo: PartInfo,
    notes: string | null,
    operatorNumber: number,
    timestamp: Date | null,
  ) => void;
  onInstallExistingPart: (
    existingPartId: number,
    parentPartId: number,
    positionId: number,
    notes: string | null,
    operatorNumber: number,
    timestamp: Date | null,
  ) => void;
  /** Whether to show Make/Model/Vendor fields. Defaults to true (EAF). Set false for Caster. */
  showMakeModelVendor?: boolean;
  /** Whether to show Serial Number field. Defaults to true. Caster only shows for Copper Mold Tube. */
  showSerialNumber?: boolean;
  /** Whether to show Warehouse/Drawing Number fields. Defaults to false. Only Caster Mold uses these. */
  showWarehouseDrawing?: boolean;
  /** Whether to validate against CASTER_EQP_INITIAL_DATETIME setting. Only for Caster mold module. */
  validateAgainstInitialDatetime?: boolean;
  /** PartId of the part currently installed at this position (swap scenario) */
  occupantPartId?: number | null;
}

export const InstallPartModal = <T extends EquipmentTreeRowFlat>({
  open,
  selectedRow,
  treeData,
  onClose,
  getParentPartId,
  onInstallNewPart,
  onInstallExistingPart,
  showMakeModelVendor = true,
  showSerialNumber = true,
  showWarehouseDrawing = false,
  validateAgainstInitialDatetime = false,
  occupantPartId,
}: Props<T>) => {
  const {
    control,
    reset,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      timestamp: null,
      isNewPart: false,
      selectedSparePart: null,
      serialNumber: "",
      make: "",
      model: "",
      vendor: "",
      warehouseNumber: "",
      drawingNumber: "",
      notes: "",
      operatorNumber: "",
    },
    mode: "onChange",
  });

  const isNewPart = watch("isNewPart");
  const selectedSparePart = watch("selectedSparePart");

  // Location lower-bound for the target position. Fetched from
  // EQPGetOperationBounds when the modal opens so the user is warned about
  // historical occupants of the position even when the position is currently
  // empty (selectedRow.InstalledDate would be null in that case).
  const [locationLowerBound, setLocationLowerBound] = useState<Date | null>(
    null,
  );

  useEffect(() => {
    if (!open || !selectedRow?.PositionId || !selectedRow?.PartTypeId) {
      setLocationLowerBound(null);
      return;
    }
    const parentPartId = getParentPartId
      ? getParentPartId(selectedRow)
      : selectedRow.EquipmentId;
    let cancelled = false;
    (async () => {
      try {
        // partId=0 because we only want the location-side floor; the part-side
        // constraints are handled by TaskDateSection via getTaskHistory.
        const bounds = await getOperationBounds(
          0,
          "install",
          parentPartId,
          selectedRow.PositionId,
        );
        if (!cancelled) setLocationLowerBound(bounds.lowerBound);
      } catch {
        if (!cancelled) setLocationLowerBound(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selectedRow, getParentPartId]);

  // Filter spare parts from tree data based on PartTypeId.
  // Supports InheritedFrom: if a spare has TypeIdentity (parsed from SQL IAm column),
  // it matches when ANY of its identity types equals the position's expected type.
  // Example: spare TypeIdentity=[1030,1070] matches position PartTypeId=1030.
  const spareParts = useMemo(() => {
    if (!selectedRow?.PartTypeId) return [];
    const expectedTypeId = selectedRow.PartTypeId;
    return treeData.filter(
      (row): row is T & SparePartTreeRow =>
        isSparePartRow(row) &&
        (row.TypeIdentity
          ? row.TypeIdentity.includes(expectedTypeId)
          : row.PartTypeId === expectedTypeId),
    );
  }, [treeData, selectedRow?.PartTypeId]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      reset({
        timestamp: moment(),
        isNewPart: false,
        selectedSparePart: null,
        serialNumber: "",
        make: "",
        model: "",
        vendor: "",
        warehouseNumber: "",
        drawingNumber: "",
        notes: "",
        operatorNumber: "",
      });
    }
  }, [open, reset]);

  const onSubmit = (data: FormData) => {
    if (!selectedRow?.PositionId || !selectedRow?.PartTypeId) return;

    const notesValue = data.notes.trim() || null;
    const operatorNum = parseInt(data.operatorNumber, 10);
    const ts = data.timestamp?.toDate() ?? null;

    // Calculate parent part ID - use callback if provided, otherwise default to EquipmentId
    const parentPartId = getParentPartId
      ? getParentPartId(selectedRow)
      : selectedRow.EquipmentId;

    if (data.isNewPart) {
      const partInfo: PartInfo = {
        Make: data.make || "",
        Model: data.model || "",
        Vendor: data.vendor || "",
        SerialNumber: data.serialNumber || "",
        WarehouseNumber: data.warehouseNumber || "",
        DrawingNumber: data.drawingNumber || "",
      };

      onInstallNewPart(
        parentPartId,
        selectedRow.PositionId,
        selectedRow.PartTypeId,
        partInfo,
        notesValue,
        operatorNum,
        ts,
      );
    } else {
      if (!data.selectedSparePart) return;

      onInstallExistingPart(
        data.selectedSparePart.L2PartId,
        parentPartId,
        selectedRow.PositionId,
        notesValue,
        operatorNum,
        ts,
      );
    }

    onClose();
  };

  const canConfirm = isNewPart || selectedSparePart !== null;

  const getSparePartLabel = (part: SparePartTreeRow) => {
    const { PartInfo: info } = part;
    const labelParts = [
      showSerialNumber ? info?.SerialNumber : null,
      ...(showMakeModelVendor ? [info?.Make, info?.Model, info?.Vendor] : []),
      info?.WarehouseNumber,
    ].filter(Boolean);
    return labelParts.join(" - ") || part.L2PartName || part.id;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Install Part - {selectedRow?.PositionName ?? ""}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Part Selection Mode */}
          <Grid item xs={12}>
            <Controller
              name="isNewPart"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Is a New Part"
                />
              )}
            />
          </Grid>

          {/* Existing Part Selector */}
          {!isNewPart ? (
            <Grid item xs={12}>
              <Controller
                name="selectedSparePart"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={spareParts}
                    value={field.value}
                    onChange={(_, newValue) => field.onChange(newValue)}
                    getOptionLabel={(option) =>
                      option ? getSparePartLabel(option) : ""
                    }
                    isOptionEqualToValue={(option, val) =>
                      option?.L2PartId === val?.L2PartId
                    }
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <li {...otherProps} key={option.L2PartId}>
                          {getSparePartLabel(option)}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Spare Part"
                        size="small"
                      />
                    )}
                    noOptionsText="No spare parts available"
                  />
                )}
              />
            </Grid>
          ) : null}

          {/* New Part Fields */}
          {isNewPart ? (
            <>
              {showSerialNumber ? (
                <Grid item xs={12}>
                  <Controller
                    name="serialNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Serial Number"
                        error={!!errors.serialNumber}
                        helperText={errors.serialNumber?.message}
                      />
                    )}
                  />
                </Grid>
              ) : null}
              {showMakeModelVendor ? (
                <>
                  <Grid item xs={4}>
                    <Controller
                      name="make"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Make"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Controller
                      name="model"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Model"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Controller
                      name="vendor"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Vendor"
                        />
                      )}
                    />
                  </Grid>
                </>
              ) : null}
              {showWarehouseDrawing ? (
                <>
                  <Grid item xs={6}>
                    <Controller
                      name="warehouseNumber"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Warehouse Number"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="drawingNumber"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Drawing Number"
                        />
                      )}
                    />
                  </Grid>
                </>
              ) : null}
            </>
          ) : null}

          {/* Date selection — for existing spares shows task history, for new parts shows location constraint only */}
          {isNewPart || selectedSparePart ? (
            <Grid item xs={12}>
              <TaskDateSection
                control={control}
                trigger={trigger}
                partId={
                  isNewPart ? null : (selectedSparePart?.L2PartId ?? null)
                }
                open={open}
                action="install"
                locationLastTimestamp={locationLowerBound}
                occupantPartId={occupantPartId}
                validateAgainstInitialDatetime={validateAgainstInitialDatetime}
              />
            </Grid>
          ) : null}

          {/* Notes */}
          <Grid item xs={12}>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Notes (optional)"
                  multiline
                  rows={2}
                  placeholder="Add any notes about this installation..."
                />
              )}
            />
          </Grid>

          {/* Operator Number */}
          <Grid item xs={12}>
            <Controller
              name="operatorNumber"
              control={control}
              rules={{
                required: "Operator number is required",
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Operator Number"
                  error={!!error}
                  helperText={error?.message || "Enter your Operator Number"}
                  inputProps={{
                    inputMode: "numeric",
                  }}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    field.onChange(value);
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
          disabled={!canConfirm}
        >
          Install
        </Button>
      </DialogActions>
    </Dialog>
  );
};
